import React, { useMemo } from 'react';
import { Box, useTheme } from '@mui/material';
import {
  Bar,
  CartesianGrid,
  ComposedChart,
  Legend,
  Line,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';
import { tokens } from '../../theme';

export interface LeadwallTimelinePoint {
  date: string;
  variant: string;
  soi: number;
  doi: number;
}

export interface DailyImpression {
  date: string;
  impressions: number;
}

interface Props {
  impressions: DailyImpression[];
  timeline: LeadwallTimelinePoint[];
}

interface CombinedRow {
  date: string;
  impressions: number | null;
  doi: number;
  soi: number;
}

const formatDateShort = (iso: string) => {
  if (iso.length !== 10) return iso;
  return `${iso.slice(8, 10)}.${iso.slice(5, 7)}.`;
};

const LeadwallOverallChart: React.FC<Props> = ({ impressions, timeline }) => {
  const theme = useTheme();
  const colors = tokens(theme.palette.mode);

  const data = useMemo<CombinedRow[]>(() => {
    const byDate = new Map<string, CombinedRow>();
    const ensure = (date: string): CombinedRow => {
      let row = byDate.get(date);
      if (!row) {
        row = { date, impressions: null, doi: 0, soi: 0 };
        byDate.set(date, row);
      }
      return row;
    };
    for (const r of impressions) ensure(r.date).impressions = r.impressions;
    for (const r of timeline) {
      const row = ensure(r.date);
      row.doi += r.doi;
      row.soi += r.soi;
    }
    return Array.from(byDate.values()).sort((a, b) => a.date.localeCompare(b.date));
  }, [impressions, timeline]);

  if (data.length === 0) {
    return (
      <Box height={340} display="flex" alignItems="center" justifyContent="center">
        Keine Daten für den gewählten Zeitraum.
      </Box>
    );
  }

  const axisColor = theme.palette.mode === 'dark' ? '#cccccc' : '#444';
  const gridColor = theme.palette.mode === 'dark' ? '#444' : '#e0e0e0';

  return (
    <Box height={340}>
      <ResponsiveContainer width="100%" height="100%">
        <ComposedChart data={data} margin={{ top: 20, right: 50, bottom: 30, left: 10 }}>
          <CartesianGrid stroke={gridColor} strokeDasharray="3 3" />
          <XAxis
            dataKey="date"
            tickFormatter={formatDateShort}
            stroke={axisColor}
            tick={{ fill: axisColor, fontSize: 12 }}
          />
          <YAxis
            yAxisId="left"
            stroke={axisColor}
            tick={{ fill: axisColor, fontSize: 12 }}
            label={{
              value: 'Impressions (GA4)',
              angle: -90,
              position: 'insideLeft',
              fill: axisColor,
              style: { fontSize: 12 },
            }}
          />
          <YAxis
            yAxisId="right"
            orientation="right"
            stroke={axisColor}
            tick={{ fill: axisColor, fontSize: 12 }}
            label={{
              value: 'SOI / DOI',
              angle: 90,
              position: 'insideRight',
              fill: axisColor,
              style: { fontSize: 12 },
            }}
          />
          <Tooltip
            contentStyle={{
              backgroundColor: theme.palette.mode === 'dark' ? '#222' : '#fff',
              borderColor: gridColor,
              fontSize: 12,
            }}
            labelFormatter={(label: string) => formatDateShort(label)}
            formatter={(value: number, name: string) => [
              value === null || value === undefined ? '—' : value.toLocaleString('de-DE'),
              name,
            ]}
          />
          <Legend wrapperStyle={{ fontSize: 12 }} />
          <Bar
            yAxisId="left"
            dataKey="impressions"
            name="Impressions"
            fill={colors.blueAccent[300]}
            opacity={0.65}
            radius={[3, 3, 0, 0]}
          />
          <Line
            yAxisId="right"
            type="monotone"
            dataKey="soi"
            name="SOI (gesamt)"
            stroke={colors.greenAccent[400]}
            strokeWidth={2}
            dot={{ r: 3 }}
          />
          <Line
            yAxisId="right"
            type="monotone"
            dataKey="doi"
            name="DOI (gesamt)"
            stroke={colors.greenAccent[700]}
            strokeWidth={2.5}
            dot={{ r: 4 }}
          />
        </ComposedChart>
      </ResponsiveContainer>
    </Box>
  );
};

export default LeadwallOverallChart;
